import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserManager, User, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { environment } from '../../../environments/environment';
import { Observable, from, of, lastValueFrom } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _router = inject(Router);
  private _http = inject(HttpClient);
  private _userManager: UserManager | null = null;

  // Public signals to track authentication state
  public readonly currentUser = signal<User | null>(null);
  public readonly isAuthenticated = signal<boolean>(false);

  constructor() {
    // Attempt to load any existing user session on startup
    this._initializeFromStorage();
  }

  /**
   * Initializes the UserManager dynamically based on the selected provider ID.
   */
  private _setupUserManager(providerId: string): void {
    const providerConfig = environment.signInProviders.find((p) => p.id === providerId);

    if (!providerConfig) {
      throw new Error(`Sign in Provider configuration for '${providerId}' not found in environment.`);
    }

    const providerAuthority = `${providerConfig.issuerUrl}/${providerConfig.userPoolId}`;

    const settings: UserManagerSettings = {
      authority: providerAuthority,
      // eslint-disable-next-line camelcase
      client_id: providerConfig.clientId,
      // eslint-disable-next-line camelcase
      redirect_uri: providerConfig.redirectUri,
      // eslint-disable-next-line camelcase
      response_type: 'code',
      scope: providerConfig.scopes,
      // Store tokens in sessionStorage to keep them isolated per tab/window
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      // Optional PKCE setting, though oidc-client-ts handles it automatically for code flow
      loadUserInfo: true
    };

    this._userManager = new UserManager(settings);

    // Setup silent renew events if needed later
    this._userManager.events.addUserLoaded((user) => {
      this.currentUser.set(user);
      this.isAuthenticated.set(true);
    });

    this._userManager.events.addUserUnloaded(() => {
      this.currentUser.set(null);
      this.isAuthenticated.set(false);
    });
  }

  /**
   * Fetches a Client Credentials token (M2M) from the backend.
   * Used for mockAuth flow to bypass UI login while still getting a real token.
   */
  private async _fetchClientCredentialsToken(): Promise<string> {
    const credentials = `${environment.mockAuth.clientId}:${environment.mockAuth.clientSecret}`;
    const authorizationHeader = `Basic ${btoa(credentials)}`;

    const body = new URLSearchParams({
      // eslint-disable-next-line camelcase
      grant_type: 'client_credentials',
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: authorizationHeader,
    });

    const response = await lastValueFrom(
      this._http.post<TokenResponse>(environment.mockAuth.tokenUrl, body.toString(), { headers })
    );
    return response.access_token;
  }

  /**
   * Tries to load the user from session storage on service initialization.
   * If we don't know the provider, we check if there's any active UserManager state.
   */
  private async _initializeFromStorage(): Promise<void> {
    // We default to the first provider for checking existing state if none is explicitly set
    if (environment.signInProviders && environment.signInProviders.length > 0) {
      this._setupUserManager(environment.signInProviders[0].id);
      try {
        const user = await this._userManager?.getUser();
        if (user && !user.expired) {
          this.currentUser.set(user);
          this.isAuthenticated.set(true);
        }
      } catch (e) {
        console.warn('Failed to restore user session:', e);
      }
    }
  }

  /**
   * Initiates the sign in redirect flow.
   */
  public async signinRedirect(providerId: string): Promise<boolean> {
    if (environment.mockAuth.enabled) {
      console.warn('Mock Auth is ENABLED. Fetching Client Credentials token instead of Sign In UI...');
      try {
        const realToken = await this._fetchClientCredentialsToken();
        const mockUser = {
          access_token: realToken,
          profile: {
            name: 'Demo Local User',
            email: 'demo@sjp.local'
          }
        } as unknown as User;

        this.currentUser.set(mockUser);
        this.isAuthenticated.set(true);
        return true;
      } catch (err) {
        console.error('Login process failed:', err);
        throw new Error('Authentication failed');
      }
    }

    this._setupUserManager(providerId);
    if (this._userManager) {
      await this._userManager.signinRedirect();
    }
    return false;
  }

  /**
   * Handles the callback from the Identity Provider.
   */
  public async signinRedirectCallback(): Promise<User> {
    // If we just landed from a redirect, we need the user manager to process the URL
    // Ensure it's initialized with the default provider or figure it out from state
    if (!this._userManager && environment.signInProviders.length > 0) {
      this._setupUserManager(environment.signInProviders[0].id);
    }

    if (!this._userManager) {
      throw new Error('UserManager not initialized during callback');
    }

    try {
      const user = await this._userManager.signinRedirectCallback();
      this.currentUser.set(user);
      this.isAuthenticated.set(true);
      return user;
    } catch (err) {
      console.error('Error during signinRedirectCallback', err);
      throw err;
    }
  }

  /**
   * Logs out the user locally and clears the session.
   * Note: This does not automatically call the IDP's end_session endpoint yet.
   */
  public async logout(): Promise<void> {
    if (this._userManager) {
      await this._userManager.removeUser();
      // Optional: await this._userManager.signoutRedirect(); to logout from IDP globally
    }
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
    this._router.navigate(['/login']);
  }

  /**
   * Retrieves the current valid access token as an Observable for Interceptors.
   */
  public getAccessToken(): Observable<string> {
    const user = this.currentUser();
    if (user && !user.expired) {
       
      return of(user.access_token);
    }
    // If token is expired or missing, handle refresh logic or throw error
    // For now, returning empty to indicate failure to get a valid token
    return of('');
  }
}
